import { useState } from 'react';
import { useSelector } from 'store';
import { captureTimerPaused } from 'utils/timecardAnalytics';
import { usePauseTrackingTimecard } from '../usePauseTrackingTimecard';
import { useStartTrackingTimecard } from '../useStartTrackingTimecard';
import PauseTrackingIconButton from './PauseTrackingIconButton';
import StartTrackingIconButton from './StartTrackingIconButton';

type Props = {
    timecard: { id: number };
};

const TimecardTrackingButton = ({ timecard }: Props): JSX.Element => {
    const { startTracking } = useStartTrackingTimecard();
    const { pauseTracking } = usePauseTrackingTimecard();
    const { trackedTimecardId, isStopwatchRunning } = useSelector((store) => store.timecard);
    const [isBusy, setIsBusy] = useState(false);
    const isCurrentlyTracking = isStopwatchRunning && trackedTimecardId && parseInt(trackedTimecardId) === timecard.id;
    // The two buttons take each other's place, and the glyph cross-fades once this one has been pressed. Until then
    // a tracked state that arrives after the button shows (restored on load, changed in another tab) is no change here.
    const [swapped, setSwapped] = useState(false);

    const runExclusively = async (action: (id: number) => Promise<unknown>) => {
        if (isBusy) {
            return;
        }
        setIsBusy(true);
        setSwapped(true);
        try {
            await action(timecard.id);
        } catch {
            // A failed start or pause is already rolled back and logged by the mutation's onQueryStarted;
            // without this catch the rejection reaches the window as an unhandled promise rejection.
        } finally {
            setIsBusy(false);
        }
    };

    return isCurrentlyTracking ? (
        <PauseTrackingIconButton
            disabled={isBusy}
            swapped={swapped}
            onClick={() => {
                void runExclusively(async (id) => {
                    if (await pauseTracking(id)) {
                        captureTimerPaused();
                    }
                });
            }}
        />
    ) : (
        <StartTrackingIconButton
            disabled={isBusy}
            swapped={swapped}
            onClick={() => {
                void runExclusively(startTracking);
            }}
        />
    );
};

export default TimecardTrackingButton;
